import moment from 'moment';
import { investBusinessAreaNestedMenu, webToolNestedMenu, corporationStatisticsChangeNestedMenu, departmentNestedMenu } from "../constants/SidebarValues";
const { Set, Map } = require('immutable');

/**
 * キーマンデータベース詳細から他画面へ遷移する際、戻るボタンで
 * 元のキーマン詳細に戻れるようにパラメータを生成する
 *
 * @example キーマンデータベース詳細からレター確認画面へ遷移し、戻るを選択した際、元のキーマンデータベース詳細が表示されるようにする
 */
export const buildGoBackToExecutiveDetailParams = (item, query, filter, filterValue) => {
  return {
    isExeDetail: true,
    relocation_personnel_id: item.id,
    isExe: true,
    query: [query],
    filter: filter,
    item: item,
    filterValue: filterValue
  };
}

/**
 * 会社関係のキーマンを検索する用のパラメータを生成する
 * @param {object} item
 * @param {string} type
 * @return {object} 会社関係のキーマンを検索する用のパラメータ
 */
export const buildCorporationRelationshipsKeymanParams = (item, type) => {
  if (type === 'rival') {
    return {
      "corporation_id": Set(item.shikiho_rival_companies),
      "rival_label": `${item.company_name}のライバル企業のキーマン`
    };
  } else if (type === 'child') {
    return {
      "child_corporation_ids": Set(item.child_corporation_ids),
      "child_corporation_ids_label": `${item.company_name}の子会社のキーマン`
    };
  } else if (type === 'parent') {
    return {
      "parent_corporation_ids": Set(item.parent_corporation_ids),
      "parent_corporation_ids_label": `${item.company_name}の親会社のキーマン`
    };
  } else if (type === 'same_department_include_parent_and_child') {
    // 子カテゴリから親カテゴリへの逆引きマップを作成
    const childToParentMap = {};
    departmentNestedMenu.forEach(parentCategory => {
      if (parentCategory && parentCategory.submenu && Array.isArray(parentCategory.submenu)) {
        parentCategory.submenu.forEach(childItem => {
          childToParentMap[childItem.value] = parentCategory.value;
        });
      }
    });

    // 子カテゴリを親カテゴリごとにグループ化
    const departmentGrouped = {};
    if (item && item.department_keys && Array.isArray(item.department_keys)) {
      item.department_keys.forEach(childValue => {
        const parentValue = childToParentMap[childValue];
        if (parentValue) {
          if (!departmentGrouped[parentValue]) {
            departmentGrouped[parentValue] = [];
          }
          departmentGrouped[parentValue].push(childValue);
        }
      });
    }

    // JavaScriptオブジェクトをimmutableのMapに変換
    const departmentMap = Map(
      Object.entries(departmentGrouped).map(([parentKey, children]) => [
        parentKey,
        Set(children)
      ])
    );

    return {
      "department": departmentMap,
      "include_parent_and_child_corporation_ids": Set(Array.from(new Set([...[item.corporation_id],...item.parent_corporation_ids, ...item.child_corporation_ids]))),
      "include_parent_and_child_corporation_ids_label": `${item.company_name}の親会社と子会社を含めたキーマン`
    };
  } else {
    throw new Error(`存在しないタイプ '${type}' が渡されました。有効なタイプは 'rival', 'child', 'parent', および 'same_department_include_parent_and_child' です。`);
  }
}

/**
 * 部門電話番号をフォーマットする
 * @param {object} item
 * @return {string}
 */
export const formatDepartmentTel = (item) => {
  const result = [];

  if (!item.department_tels) return null;

  for (const key in item.department_tels) {
    // TODO 新しいESができたら、typeof item.department_tels[key] === 'object'の条件は不要になるので削除(リリース当日からESが作られる間を考慮している)
    if (item.department_tels.hasOwnProperty(key) && typeof item.department_tels[key] === 'object') {
      const telNumbers = item.department_tels[key].map(item => item.tel ? item.tel : "").join(', ');
      result.push(`${key}: ${telNumbers}`);
    }
  }

  return result.join('\n');
}

/**
 * 日付範囲フォーマッター生成関数
 * @param {string} type
 * @returns {string}
 */
export const createDateRangeFormatter = (type) => (filterValue, entry, label) => {
  const config = {
    sent_status: {
      only: {
        from: 'sent_only_from',
        to: 'sent_only_to',
        text: '注文済みキーマンのみを表示'
      },
      except: {
        from: 'sent_except_from',
        to: 'sent_except_to',
        text: '注文済みキーマンを除外'
      }
    },
    corporation_sent_status: {
      only: {
        from: 'corporation_sent_only_from',
        to: 'corporation_sent_only_to',
        text: '注文済み企業のみを表示'
      },
      except: {
        from: 'corporation_sent_except_from',
        to: 'corporation_sent_except_to',
        text: '注文済み企業を除外'
      }
    },
    follow_call_order_status: {
      only: {
        from: 'follow_call_order_only_from',
        to: 'follow_call_order_only_to',
        text: 'フォローコール依頼済みのみを表示'
      },
      except: {
        from: 'follow_call_order_except_from',
        to: 'follow_call_order_except_to',
        text: 'フォローコール依頼済みを除外'
      }
    }
  }[type];

  if (!config) return '';

  const parentName = entry ? entry.parent_name : '';
  let rangeDate = '';

  ['only', 'except'].forEach(mode => {
    const { from, to, text } = config[mode];
    if (((parentName === text) || (label === text)) &&
      filterValue && filterValue[from] && filterValue[to]) {
      rangeDate = `（${moment(filterValue[from]).format('YYYY年MM月DD日')} - ${moment(filterValue[to]).format('YYYY年MM月DD日')}）`;
    }
  });

  return rangeDate;
};

/**
 * インテントキーワードの設定
 */
const INTENT_KEYWORD_CONFIGS = {
  invest_business_area: {
    menu: investBusinessAreaNestedMenu,
  },
  web_tool: {
    menu: webToolNestedMenu,
  },
  corporation_statistics_change: {
    menu: corporationStatisticsChangeNestedMenu,
  }
};

/**
 * filterValueから関連するプロパティを抽出するヘルパー関数
 * @param {object} filterValue
 * @returns {object}
 */
export const getIntentKeywordMaps = (filterValue) => {
  return Object.keys(INTENT_KEYWORD_CONFIGS).reduce((acc, key) => {
    if (filterValue && filterValue[key]) {
      acc[key] = filterValue[key];
    }
    return acc;
  }, {});
};

/**
 * インテントキーワードを抽出するヘルパー関数
 * @param {string} reactionKeyword
 * @param {object} propertyMaps
 * @returns {string[]}
 */
export const formatIntentKeywords = (reactionKeyword, propertyMaps = {}) => {
  // 初期キーワード配列の作成
  const keywords = reactionKeyword
    /* eslint-disable no-irregular-whitespace */
    ? reactionKeyword.trim().split(/[\s,　・]+/)
    : [];

  // プロパティマップの処理を最適化
  return Object.entries(propertyMaps).reduce((acc, [key, propertyMap]) => {
    const config = INTENT_KEYWORD_CONFIGS[key];
    if (!config || !propertyMap || !propertyMap.count()) return acc;

    // キャッシュ用のマップを作成
    const menuCache = new Map(config.menu.map(item => [item.value, item]));

    // 子カテゴリの名前を効率的に取得
    const newKeywords = propertyMap.toArray().flatMap(([parentKey, childValues]) => {
      const parentCategory = menuCache.get(parentKey);
      if (!parentCategory) return [];

      // 子カテゴリのキャッシュを作成
      const submenuCache = new Map(parentCategory.submenu.map(item => [item.value, item.name]));

      return Array.from(childValues)
        .map(childValue => submenuCache.get(childValue))
        .filter(Boolean);
    });

    return newKeywords.length > 0 ? [...acc, ...newKeywords] : acc;
  }, keywords);
};

/**
 * 空データの場合に[0]を渡すヘルパー関数
 * バックエンドのエラーを回避するため、空の配列やnullの場合は存在しないID: 0を渡す
 * @param {Array|null|undefined} data - チェック対象のデータ
 * @returns {Array} データが存在する場合はそのまま、空の場合は[0]を返す
 */
export const ensureNonEmptyArray = (data) => {
  if (!data || (Array.isArray(data) && data.length === 0)) {
    return [0];
  }
  return data;
};

/**
 * 会社関係キーマン検索の共通パラメータを生成するヘルパー関数
 * @param {Array} ids - 検索対象のID配列
 * @param {string} idKey - IDのキー名（corporation_id, parent_corporation_ids, child_corporation_ids）
 * @returns {object} API呼び出し用のパラメータオブジェクト
 */
export const createCorporationKeymanSearchParams = (ids, idKey) => {
  return {
    [idKey]: ensureNonEmptyArray(ids),
    "per_page": 6,
    "paging": 1,
    "page": 1,
  };
};



// WEBPACK FOOTER //
// ./src/utils/executiveListUtils.js